'use client';

import { useEffect, useMemo, useState } from 'react';
import type { IntelligenceReport } from '@/lib/unifiedIntelligence';
import { getWeeklyMissions, type Mission } from '@/lib/gamification';
import { getCompletedMissionIds } from '@/lib/progressionService';
import { getRecommendedArchetype } from '@/lib/archetypeSystem';
import { loadProgressionState } from '@/lib/progressionService';
import { useAuth } from '@/lib/AuthContext';
import { Target, CheckCircle2, Circle } from 'lucide-react';
import type { DBProgressionState } from '@/lib/db';
import HudPanel from '@/components/ui/HudPanel';

export default function WeeklyMissionsCard({ intelligence }: { intelligence: IntelligenceReport }) {
  const { user } = useAuth();
  const [prog, setProg] = useState<DBProgressionState | null>(null);
  const [completedMissionIds, setCompletedMissionIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const recommendedArchetype = useMemo(() =>
    prog ? getRecommendedArchetype(prog) : null,
    [prog]
  );

  const missions = useMemo(() =>
    getWeeklyMissions(intelligence, completedMissionIds, recommendedArchetype?.archetypeId),
    [intelligence, completedMissionIds, recommendedArchetype]
  );

  useEffect(() => {
    const loadData = async () => {
      if (!user?.uid) { setLoading(false); return; }
      try {
        const [state, ids] = await Promise.all([
          loadProgressionState(user.uid),
          getCompletedMissionIds(user.uid),
        ]);
        setProg(state);
        setCompletedMissionIds(ids);
      } catch { /* non-critical */ }
      setLoading(false);
    };
    loadData();
  }, [user]);

  if (loading) return null;

  const completedCount = missions.filter(m => m.completed).length;
  const totalCount = missions.length;

  if (totalCount === 0) return null;

  const tagColors: Record<string, string> = {
    strength: 'text-red-400',
    endurance: 'text-blue-400',
    bodyweight: 'text-green-400',
    recovery: 'text-purple-400',
    consistency: 'text-amber-400',
  };

  return (
    <HudPanel
      variant="standard"
      severity="gold"
      badge="WEEKLY MISSIONS"
      code=""
      callout={
        <span className="text-operator-gold tabular-nums">{completedCount}/{totalCount}</span>
      }
    >
      <div className="space-y-2.5">
        {missions.map(mission => (
          <div
            key={mission.id}
            className={`flex items-start gap-2.5 ${mission.completed ? 'opacity-50' : ''}`}
          >
            {mission.completed ? (
              <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
            ) : (
              <Circle className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className={`text-xs font-semibold ${mission.completed ? 'text-slate-400 line-through' : 'text-slate-200'}`}>
                  {mission.title}
                </span>
                <span className={`text-[9px] uppercase font-bold ${tagColors[mission.tag] || 'text-slate-400'}`}>
                  {mission.tag}
                </span>
              </div>
              <p className="text-[10px] text-slate-400 mt-0.5 leading-snug">{mission.task}</p>
            </div>
            <span className="font-mono text-[10px] text-operator-gold flex-shrink-0 mt-0.5 tabular-nums">
              +{mission.xp}
            </span>
          </div>
        ))}
      </div>

      {/* Progress bar */}
      <div className="mt-3 h-1 bg-operator-steel/30 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-operator-gold/60 to-operator-gold rounded-full transition-all"
          style={{ width: `${totalCount > 0 ? (completedCount / totalCount) * 100 : 0}%` }}
        />
      </div>
    </HudPanel>
  );
}
